import { createContext, useContext, useState, useEffect } from 'react';
import { useLanguage } from './LanguageContext';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
};

// Pricing strategy: prix nominaux alignés EUR/USD (psychological pricing).
// 9.99 € = 9.99 $, pas de conversion mathématique. Standard SaaS B2C
// international (Spotify, Notion, etc.).
const EUR_TO_USD = {
  9.99: 9.99,
  29.99: 29.99,
  39.99: 39.99,
  19.99: 19.99,
  59.98: 59.98,
};

const STORAGE_KEY = 'bpm_currency';
const SUPPORTED = ['EUR', 'USD'];

const detectInitialCurrency = (language) => {
  // Manual override wins
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && SUPPORTED.includes(saved)) return saved;
  // English + US/CA browser → USD; everything else → EUR
  if (language === 'en') {
    const nav = (navigator.language || '').toLowerCase();
    if (nav.startsWith('en-us') || nav.startsWith('en-ca')) return 'USD';
  }
  return 'EUR';
};

export const CurrencyProvider = ({ children }) => {
  const { language } = useLanguage();
  const [currency, setCurrencyState] = useState(() => detectInitialCurrency(language));

  // Re-evaluate auto-detection when language changes IF user has no manual override yet
  useEffect(() => {
    const hasOverride = localStorage.getItem(STORAGE_KEY);
    if (!hasOverride) {
      setCurrencyState(detectInitialCurrency(language));
    }
  }, [language]);

  const setCurrency = (next) => {
    if (!SUPPORTED.includes(next)) return;
    localStorage.setItem(STORAGE_KEY, next);
    setCurrencyState(next);
  };

  const convert = (amountEur) => {
    if (currency === 'EUR') return amountEur;
    if (amountEur in EUR_TO_USD) return EUR_TO_USD[amountEur];
    // Fallback : nominal alignment (psychological pricing strategy)
    return amountEur;
  };

  const format = (amountEur) => {
    const value = convert(amountEur);
    const locale = language === 'fr' ? 'fr-FR' : 'en-US';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (e) {
      const symbol = currency === 'USD' ? '$' : '€';
      return `${symbol}${value.toFixed(2)}`;
    }
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, convert, format }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
